import React, { useEffect, useState } from "react";
import { Autocomplete, Avatar, Box, Chip, Skeleton, TextField, Typography } from '@mui/material';
import PersonOutlined from '@mui/icons-material/PersonOutlined';
import GroupsOutlined from '@mui/icons-material/GroupsOutlined';
import WorkOutlineOutlined from '@mui/icons-material/WorkOutlineOutlined';
import {
  addCandidatSelection,
  editCandidatSelection,
} from "../../../services/SelectionCandidatService";
import { getUserData } from "../../../utils/FunctionHelpers";
import { getUsersByPairs } from "../../../services/UserService";
import { getMissionsActive } from "../../../services/MissionService";
import { withRouter } from "react-router-dom/cjs/react-router-dom";
import { AppButton } from "../../../components/UI";
import Modal from "../../../components/Modal/Modal";

const NAME_MAX = 80;

const initials = (name) =>
  (name || '?').split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join('');

// "BELLIER Gérard" and "Gérard Bellier" are the same person.
const sameName = (a, b) => {
  const norm = (s) => (s || '').toLowerCase().split(/\s+/).filter(Boolean).sort().join(' ');
  return !!a && norm(a) === norm(b);
};

const missionLabel =(m) => (m ? `${m.nom_societe ? `${m.nom_societe} - ` : ''}${m.value ?? ''}` : '');

const FieldLabel = ({ children, required, hint, error }) => (
  <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 0.75 }}>
    <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: error ? '#dc2626' : '#334155', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
      {children} {required && <Box component="span" sx={{ color: '#dc2626' }}>*</Box>}
    </Typography>
    {hint && <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8' }}>{hint}</Typography>}
  </Box>
);

const ErrorText = ({ children }) => children ? (
  <Typography sx={{ fontSize: '0.78rem', color: '#dc2626', mt: 0.5 }}>{children}</Typography>
) : null;

const inputSx = { '& .MuiOutlinedInput-root': { borderRadius: '10px', background: '#fff' } };

const SelectionModal = ({
  selectedCandidats, history,
  snackbarShowMessage,
  children,
  ...otherProps
}) => {
  const loggedUser = {
    id: getUserData().uuid,
    value: getUserData().lastname + " " + getUserData().firstname,
  };
  const {
    isModify,
    idSelection,
    oldSelectionName,
    oldCreator,
    oldMission,
    setReloadSelections,
    reloadSelections,
    isImport,
    setImportData,
  } = otherProps;
  const [modalState, setModalState] = useState({ open: false });
  const [loading, setLoading] = useState(false);
  const [touched, setTouched] = useState(false);
  const [selectionData, setSelectionData] = useState({ selectionName: '', creator: null, mission: null });
  const [users, setUsers] = useState({ isLoading: false, data: [] });
  const [missions, setMissions] = useState({ isLoading: false, data: [] });

  const setField = (name, value) => {
    setTouched(true);
    setSelectionData((prev) => ({ ...prev, [name]: value }));
  };

  const errors = {
    nomSelection: selectionData.selectionName?.trim() ? "" : "Donnez un nom à la sélection.",
    sourceur: selectionData.creator?.id ? "" : "Choisissez le talent manager.",
  };
  const isValid = !errors.nomSelection && !errors.sourceur;
  const showError = (field) => (touched ? errors[field] : "");

  const handleOpen = () => {
    // Fill the form before the first render so the fields are controlled from the start
    // (an Autocomplete that starts with `undefined` ignores the value set afterwards).
    setSelectionData({
      selectionName: isModify ? oldSelectionName || '' : '',
      creator: isModify ? (oldCreator?.id || oldCreator?.value ? oldCreator : null) : loggedUser,
      mission: isModify && oldMission ? oldMission : null,
    });
    setTouched(false);
    setModalState({ open: true });
  };
  const handleClose = () => { if (!loading) setModalState({ open: false }); };

  const buildPayload = () => ({
    ...selectionData,
    selectionName: selectionData.selectionName.trim(),
    selectedCandidats,
    creator: selectionData.creator.id,
    mission: selectionData.mission?.id,
  });

  const handleUpdate = () => {
    setTouched(true);
    if (!isValid) return;
    snackbarShowMessage("Modification de la sélection...", "warning");
    setLoading(true);
    editCandidatSelection(buildPayload(), idSelection)
      .then(() => {
        snackbarShowMessage("La sélection a été modifiée avec succès.");
        setReloadSelections(!reloadSelections);
        setModalState({ open: false });
      })
      .catch(({ response }) => {
        snackbarShowMessage(response?.data?.error?.detail || "Une erreur est survenue.", "error", 4000);
      })
      .finally(() => setLoading(false));
  };

  const handleSubmit = () => {
    setTouched(true);
    if (!isValid) return;
    snackbarShowMessage("Création de la sélection...", "warning", null);
    setLoading(true);
    addCandidatSelection(buildPayload())
      .then(({ data }) => {
        if (isImport) {
          setReloadSelections(!reloadSelections);
          setImportData((prevState) => ({ ...prevState, selection: data }));
        }
        snackbarShowMessage("La sélection a été crée avec succès.", "success", 4000);
        history.push({
          pathname: `/selections/${data.id}/candidats/list`,
          search: `?selectionName=${data.selectionName}&quantity=${selectedCandidats.length}&consultant=${selectionData.creator.value}`
        });
      })
      .catch(({ response }) => {
        snackbarShowMessage(response?.data?.error?.detail || "Une erreur est survenue.", "error", 4000);
      })
      .finally(() => {
        setLoading(false);
        setModalState({ open: false });
      });
  };

  const save = () => (isModify ? handleUpdate() : handleSubmit());

  useEffect(() => {
    if (!modalState.open) return;
    let active = true;
    setUsers((prev) => ({ ...prev, isLoading: true }));
    setMissions((prev) => ({ ...prev, isLoading: true }));
    getUsersByPairs()
      .then(({ data }) => {
        if (!active) return;
        setUsers({ isLoading: false, data: [...data].sort((a, b) => (a.value || '').localeCompare(b.value || '', 'fr')) });
        // Swap the saved talent manager for the matching user from the list (by id, or by name if the row has no id).
        setSelectionData((prev) => {
          if (!prev.creator) return prev;
          const match = data.find((u) => (prev.creator.id && u.id === prev.creator.id) || sameName(u.value, prev.creator.value));
          return match ? { ...prev, creator: match } : prev;
        });
      })
      .catch(() => {
        if (!active) return;
        setUsers({ isLoading: false, data: [] });
        snackbarShowMessage("Erreur de chargements des utilisateurs.", "error", 4000);
      });
    getMissionsActive()
      .then(({ data }) => active && setMissions({ isLoading: false, data }))
      .catch(() => {
        if (!active) return;
        setMissions({ isLoading: false, data: [] });
        snackbarShowMessage("Erreur de chargements des missions actives.", "error", 4000);
      });
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalState.open]);

  const count = selectedCandidats?.length ?? 0;
  const isMe = selectionData.creator?.id === loggedUser.id;

  const body = (
    <Box
      component="form"
      onSubmit={(e) => { e.preventDefault(); save(); }}
      sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}
    >
      {!isModify && count > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 1.75, py: 1.25, borderRadius: '10px', background: '#f0f7fd', border: '1px solid #aed6f1' }}>
          <GroupsOutlined sx={{ color: '#03407b' }} />
          <Typography sx={{ fontSize: '0.9rem', color: '#03407b' }}>
            <b>{count} candidat{count > 1 ? 's' : ''}</b> {count > 1 ? 'seront ajoutés' : 'sera ajouté'} à cette sélection
          </Typography>
        </Box>
      )}

      <Box>
        <FieldLabel required error={showError('nomSelection')} hint={`${selectionData.selectionName.length}/${NAME_MAX}`}>
          Nom de la sélection
        </FieldLabel>
        <TextField
          autoFocus
          fullWidth
          autoComplete="off"
          placeholder="Ex : Développeurs Java – Paris"
          value={selectionData.selectionName}
          onChange={(e) => setField('selectionName', e.target.value.slice(0, NAME_MAX))}
          error={!!showError('nomSelection')}
          sx={inputSx}
        />
        <ErrorText>{showError('nomSelection')}</ErrorText>
      </Box>

      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <FieldLabel required error={showError('sourceur')}>Talent manager</FieldLabel>
          {!isMe && (
            <Chip
              icon={<PersonOutlined sx={{ fontSize: 16 }} />}
              label="M'affecter"
              size="small"
              onClick={() => setField('creator', loggedUser)}
              sx={{ mb: 0.75, height: 24, fontWeight: 600, color: '#03407b', background: '#ebf4fb', border: '1px solid #aed6f1', '&:hover': { background: '#dbeefa' } }}
            />
          )}
        </Box>
        {users.isLoading && !selectionData.creator ? (
          <Skeleton variant="rounded" height={44} sx={{ borderRadius: '10px' }} />
        ) : (
          <Autocomplete
            disableClearable
            options={users.data}
            loading={users.isLoading}
            value={selectionData.creator}
            onChange={(e, value) => setField('creator', value)}
            getOptionLabel={(o) => o?.value ?? ''}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            noOptionsText="Aucun utilisateur trouvé"
            renderOption={({ key, ...props }, o) => (
              <Box component="li" key={o.id} {...props} sx={{ display: 'flex', gap: 1.25 }}>
                <Avatar sx={{ width: 26, height: 26, fontSize: '0.7rem', fontWeight: 700, bgcolor: '#ebf4fb', color: '#03407b' }}>{initials(o.value)}</Avatar>
                {o.value}
                {o.id === loggedUser.id && <Chip label="Moi" size="small" sx={{ height: 20, fontSize: '0.7rem', ml: 'auto', background: '#f1f5f9' }} />}
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Rechercher un talent manager…"
                error={!!showError('sourceur')} slotProps={{ ...params.slotProps, input: { ...params.slotProps?.input, ...({
                  ...params.slotProps.input,
                  startAdornment: selectionData.creator ? (
                    <Avatar sx={{ width: 26, height: 26, ml: 0.5, fontSize: '0.7rem', fontWeight: 700, bgcolor: '#ebf4fb', color: '#03407b' }}>
                      {initials(selectionData.creator.value)}
                    </Avatar>
                  ) : null,
                }) } }}
              />
            )}
            sx={inputSx}
          />
        )}
        <ErrorText>{showError('sourceur')}</ErrorText>
      </Box>

      <Box>
        <FieldLabel hint="Optionnel">Mission</FieldLabel>
        <Autocomplete
          options={missions.data}
          loading={missions.isLoading}
          value={selectionData.mission}
          onChange={(e, value) => setField('mission', value)}
          getOptionLabel={missionLabel}
          isOptionEqualToValue={(o, v) => o.id === v.id}
          noOptionsText="Aucune mission active trouvée"
          renderOption={({ key, ...props }, o) => (
            <Box component="li" key={o.id} {...props} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start !important' }}>
              <Typography sx={{ fontSize: '0.92rem', fontWeight: 600, color: '#0f172a' }}>{o.value}</Typography>
              {o.nom_societe && <Typography sx={{ fontSize: '0.78rem', color: '#64748b' }}>{o.nom_societe}</Typography>}
            </Box>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Lier la sélection à une mission active…" slotProps={{ ...params.slotProps, input: { ...params.slotProps?.input, ...({
                ...params.slotProps.input,
                startAdornment: <WorkOutlineOutlined sx={{ color: '#94a3b8', fontSize: 20, ml: 0.5 }} />,
              }) } }}
            />
          )}
          sx={inputSx}
        />
      </Box>

      {/* Lets Enter submit the form from the name field */}
      <button type="submit" hidden aria-hidden="true" />
    </Box>
  );

  const actions = (
    <>
      <AppButton variant="secondary" onClick={handleClose} disabled={loading}>
        Annuler
      </AppButton>
      <AppButton variant="primary" onClick={save} disabled={loading || (touched && !isValid)} loading={loading}>
        {isModify ? "Modifier" : "Sauvegarder"}
      </AppButton>
    </>
  );

  return (
    <>
      {children(handleOpen)}
      <Modal
        open={!!modalState.open}
        onClose={handleClose}
        title={isModify ? "Modifier une sélection" : "Créer une sélection"}
        maxWidth="sm"
        actions={actions}
      >
        {body}
      </Modal>
    </>
  );
};

export default withRouter(SelectionModal);
